import React from 'react';
import { useRouter } from 'next/router';
import { TextInputField } from '@havenengineering/module-shared-library/dist/form/components/TextInput';
import useForm, {
  createRegexValidator,
  Validator,
} from '@havenengineering/module-shared-library/dist/form/hooks/useForm';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';
import clsx from 'clsx';

import { formErrorHandling } from '../../../helpers/googleTag';
import { getRefundType } from '../../../helpers/refund';
import { RefundForm } from '../../../types/refund';
import SortCodeInputField from '../SortCodeInputField';

type RefundStepForm = Pick<
  RefundForm,
  'amount' | 'totalAmount' | 'fullName' | 'bankAccountNo' | 'sortCode'
>;
interface RefundStepProps {
  state: RefundForm;
  handleStepChange: (changedForm: RefundStepForm) => void;
}

export const Refund = ({ state, handleStepChange }: RefundStepProps) => {
  const { loggedInUser } = useAuthContext();
  const isUserLoggedIn = !!loggedInUser;
  const router = useRouter();
  const { type } = router.query;
  const isLettingIncomeRefund = type === 'letting-income';

  const amountValidator: Validator<string> = (value: string) => {
    const minNumber = isLettingIncomeRefund ? 5 : 100;
    return parseFloat(value) < minNumber
      ? `Must be higher than £${minNumber}`
      : undefined;
  };

  const { fields, submit } = useForm<RefundStepForm>({
    initialState: {
      amount: state?.amount || '0',
      totalAmount: state?.totalAmount || '0',
      fullName: state?.fullName || '',
      bankAccountNo: state?.bankAccountNo || '',
      sortCode: state?.sortCode || '',
    },
    schema: {
      amount: {
        required: true,
        validator: amountValidator,
      },
      totalAmount: {
        required: !isUserLoggedIn,
      },
      fullName: {
        required: true,
      },
      bankAccountNo: {
        required: true,
        validator: [
          createRegexValidator(
            /^[0-9]{8,8}$/,
            'Bank Account Number must be 8 numbers without any spaces'
          ),
        ],
      },
      sortCode: {
        required: true,
        validator: [
          createRegexValidator(
            /^[0-9]{2,2}-[0-9]{2,2}-[0-9]{2,2}$/,
            'Sort Code must be 6 numbers'
          ),
        ],
      },
    },
    submitCallback: async (state) => {
      handleStepChange(state);
    },
    errorCallback: (fields) => {
      const errors = Object.keys(fields).map((key: string) => {
        const field = fields[key as keyof RefundStepForm];
        return {
          getErrorMessage: () => field.error,
          props: {
            name: field.name,
            label: '',
          },
        };
      }) as any[];
      formErrorHandling(getRefundType(isLettingIncomeRefund), errors);
    },
  });

  const totalAmount = Number(state.totalAmount).toFixed(2);
  const isCreditBalance = isLettingIncomeRefund
    ? Number(state.totalAmount) > 0
    : Number(state.totalAmount) < 0;
  const balanceLabel = isCreditBalance
    ? '(credit balance)'
    : Number(state.totalAmount) === 0
    ? ''
    : '(debit balance)';

  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <hr />
      <p className="owners-body">
        Please enter the amount you’re applying to have refunded directly to
        your bank account (You can check the amount on the finance section)
      </p>
      <TextInputField
        className="fs-mask"
        id="amount"
        label="Amount (£)"
        field={fields.amount}
        type="number"
      />
      <hr />
      {isUserLoggedIn ? (
        <p className={clsx('owners-body', 'fs-mask')}>
          <strong>
            {`Your current balance: £${totalAmount} ${balanceLabel}`}
          </strong>
        </p>
      ) : (
        <>
          <p className="owners-body">
            Please enter your current credit balance on your owner account from
            your most recent statement or notice of payment (A credit balance is
            shown as a minus amount e.g. -£450)
          </p>
          <TextInputField
            className="fs-mask"
            id="amount"
            label="Total Amount (£)"
            field={fields.totalAmount}
            type="number"
          />
        </>
      )}
      <p className="owners-body">
        Please enter the bank account details for where you’d like the refund to
        be transferred if it’s approved
      </p>
      <TextInputField
        className="fs-mask"
        id="amount"
        label="Name"
        field={fields.fullName}
      />
      <TextInputField
        className="fs-mask"
        id="amount"
        label="Bank Account no"
        field={fields.bankAccountNo}
        type="number"
      />
      <SortCodeInputField
        id="amount"
        label="Sort Code"
        field={fields.sortCode}
      />
      <Button type="submit" fullWidth onClick={() => submit()}>
        Continue
      </Button>
    </form>
  );
};
